import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Search, ChevronRight, Users, Globe, BookOpen, Layers, MessageCircle, Phone, Video, Shield, Award, TrendingUp, Star, CheckCircle, Lock, RefreshCw, Heart, Send } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function SuggestionBox() {
  const [msg, setMsg] = useState('');
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const submit = async () => {
    if (!msg.trim()) return;
    setSending(true);
    try {
      await fetch(`${API_URL}/api/suggestions/anonymous`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg, email, category: 'landing' }),
      });
      setSent(true);
    } catch {}
    setSending(false);
  };
  if (sent) return <div className="bg-zinc-900 border border-emerald-500/30 rounded-2xl p-6 text-center"><CheckCircle className="w-8 h-8 text-emerald-400 mx-auto mb-2" /><p className="text-sm text-white font-semibold">Thank you! We read every suggestion.</p></div>;
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-6 text-left" data-testid="suggestion-box">
      <textarea value={msg} onChange={e => setMsg(e.target.value)} placeholder="What would make Connect better for you?" rows={3}
        className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-sm outline-none focus:border-zinc-600 text-white placeholder-zinc-600 resize-none mb-3" data-testid="suggestion-input" />
      <div className="flex items-center gap-3">
        <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email (optional)"
          className="flex-1 bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-zinc-600 text-white placeholder-zinc-600" />
        <button onClick={submit} disabled={!msg.trim() || sending}
          className="bg-white text-black font-bold text-sm rounded-xl px-6 py-2.5 hover:bg-zinc-200 transition-colors disabled:opacity-30 flex items-center gap-2" data-testid="suggestion-submit">
          <Send className="w-4 h-4" />{sending ? 'Sending...' : 'Send'}
        </button>
      </div>
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [taxonomy, setTaxonomy] = useState({});
  const [stats, setStats] = useState({});
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    setIsLoggedIn(!!localStorage.getItem('token'));
    Promise.all([
      fetch(`${API_URL}/api/taxonomy`).then(r => r.ok ? r.json() : {}),
      fetch(`${API_URL}/api/taxonomy/stats`).then(r => r.ok ? r.json() : {}),
    ]).then(([tax, st]) => { setTaxonomy(tax); setStats(st); }).catch(() => {});
  }, []);

  const industryCount = Object.keys(taxonomy).length || 49;
  const nodeCount = stats.total_nodes || 4990;

  return (
    <div className="min-h-screen bg-black text-white selection:bg-purple-500/30" data-testid="landing-page">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-black/80 backdrop-blur-xl border-b border-zinc-800/50" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-9 h-9 bg-gradient-to-br from-purple-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/20">
              <span className="text-white font-extrabold text-sm">C</span>
            </div>
            <span className="font-extrabold text-xl bg-gradient-to-r from-purple-400 to-orange-400 bg-clip-text text-transparent">Connect</span>
          </div>
          <div className="flex items-center space-x-4">
            {isLoggedIn ? (
              <button onClick={() => navigate('/app')} className="bg-white text-black font-bold text-sm rounded-full px-5 py-2 hover:bg-zinc-200 transition-colors" data-testid="nav-open-app">Open App</button>
            ) : (
              <>
                <button onClick={() => navigate('/login')} className="text-sm font-semibold text-zinc-400 hover:text-white transition-colors" data-testid="nav-login">Log In</button>
                <button onClick={() => navigate('/signup')} className="bg-white text-black font-bold text-sm rounded-full px-5 py-2 hover:bg-zinc-200 transition-all shadow-[0_0_20px_rgba(168,85,247,0.15)]" data-testid="nav-signup">Get Started</button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* ════════════════ HERO ════════════════ */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-purple-500/[0.04] rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-orange-500/[0.04] rounded-full blur-[100px]" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-20 sm:pt-24 sm:pb-28 relative">
          <div className="max-w-3xl">
            <div className="inline-flex items-center space-x-2 bg-zinc-900/80 border border-zinc-800 rounded-full px-4 py-1.5 mb-8">
              <Globe className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-xs text-zinc-400">{industryCount} industries</span>
              <span className="w-1 h-1 rounded-full bg-zinc-700" />
              <span className="text-xs text-zinc-400">{nodeCount.toLocaleString()} micro-subjects</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.08] mb-6 tracking-tight">
              Learn and teach anything,<br/>
              <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-orange-400 bg-clip-text text-transparent">anytime, anywhere.</span>
            </h1>

            <p className="text-base sm:text-lg text-zinc-400 max-w-xl mb-4 leading-relaxed">
              Learn from exclusive experts — free content, paid 1-on-1s. Personalised, fast, value for money. Save time, energy, and money by talking to someone who already did it.
            </p>
            <p className="text-sm text-zinc-500 max-w-lg mb-8">
              {nodeCount.toLocaleString()} subjects. {industryCount} industries. From ancient Vedic mathematics to quantum computing. If it can be taught, it lives here.
            </p>

            <div className="flex flex-col sm:flex-row items-start gap-3 mb-10">
              <button onClick={() => navigate('/signup')}
                className="bg-white text-black font-bold text-base rounded-full px-8 py-3.5 hover:bg-zinc-200 transition-all flex items-center space-x-2 shadow-[0_0_30px_rgba(168,85,247,0.15)]"
                data-testid="hero-cta">
                <span>Claim Your Expertise</span>
                <ArrowRight className="w-5 h-5" />
              </button>
              <button onClick={() => navigate('/explore')}
                className="bg-zinc-900 border border-zinc-800 text-white font-semibold text-base rounded-full px-8 py-3.5 hover:bg-zinc-800 transition-colors flex items-center space-x-2"
                data-testid="hero-explore">
                <BookOpen className="w-5 h-5" />
                <span>Browse {nodeCount.toLocaleString()} Subjects</span>
              </button>
            </div>

            <div className="flex flex-wrap gap-8 text-sm">
              <div>
                <div className="text-2xl sm:text-3xl font-extrabold text-white">{industryCount}</div>
                <div className="text-zinc-500 text-xs mt-0.5">Industries</div>
              </div>
              <div className="w-px bg-zinc-800 self-stretch" />
              <div>
                <div className="text-2xl sm:text-3xl font-extrabold bg-gradient-to-r from-purple-400 to-orange-400 bg-clip-text text-transparent">{nodeCount.toLocaleString()}</div>
                <div className="text-zinc-500 text-xs mt-0.5">Micro-Subjects You Can Teach & Learn</div>
              </div>
              <div className="w-px bg-zinc-800 self-stretch" />
              <div>
                <div className="text-2xl sm:text-3xl font-extrabold text-white">₹0</div>
                <div className="text-zinc-500 text-xs mt-0.5">To Start Teaching</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════ PHILOSOPHY ════════════════ */}
      <section className="py-16 sm:py-24 border-t border-zinc-900 relative overflow-hidden" data-testid="philosophy-section">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-500/[0.02] to-transparent" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 relative">
          <div className="space-y-10">
            {[
              { quote: "The only thing in the world that increases by giving is knowledge.", sub: "When you teach, you don't lose what you know. You multiply it." },
              { quote: "Modern education systems were never designed to have limitations on who can teach.", sub: "A certification doesn't make you wise. Experience does. A grandmother who survived 80 winters knows something no professor can teach." },
              { quote: "Everybody has the right to educate. And everybody has the right to learn from anyone.", sub: "Not from algorithms. Not from AI. From real humans with real stories and real scars." },
            ].map((item, i) => (
              <div key={i} className="relative">
                <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-purple-500/40 to-orange-500/40 rounded-full" />
                <blockquote className="pl-6">
                  <p className="text-xl sm:text-2xl font-bold text-white leading-snug mb-2">{item.quote}</p>
                  <p className="text-sm text-zinc-500 leading-relaxed max-w-2xl">{item.sub}</p>
                </blockquote>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════ WHAT MAKES US DIFFERENT ════════════════ */}
      <section className="py-16 sm:py-20 border-t border-zinc-900" data-testid="difference-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-600 mb-3">Why this exists</p>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-4 max-w-2xl leading-tight">
            Google can't teach you what an expert learned in 20 years of doing it.
          </h2>
          <p className="text-sm text-zinc-500 max-w-xl mb-12">
            Reading is not learning. Watching is not understanding. The fastest way to learn anything is to talk to someone who already did it. Connect makes that possible for every subject on Earth.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { title: "Not another course platform", desc: "No pre-recorded videos. No slides. Real 1-on-1 conversations with real people who actually do the thing.", accent: "from-purple-500/20 to-purple-600/5" },
              { title: "Every skill has a home", desc: `${nodeCount.toLocaleString()} micro-subjects — from Bharatanatyam to Blockchain, from Kalaripayattu to Kubernetes, from Madhubani painting to Machine Learning.`, accent: "from-emerald-500/20 to-emerald-600/5" },
              { title: "Your knowledge pays you", desc: "Set your price. Get paid per message, voice note, or video call. No middleman taking 50%. You keep what you earn.", accent: "from-orange-500/20 to-orange-600/5" },
              { title: "No algorithms, no AI matching", desc: "We don't need AI to tell you who to learn from. Our discovery engine uses real signals — what people actually pay for, book, and come back to.", accent: "from-blue-500/20 to-blue-600/5" },
              { title: "For every human, not just influencers", desc: "A retired teacher. A local craftsman. A college student who cracked a tough exam. Everyone has expertise worth sharing.", accent: "from-pink-500/20 to-pink-600/5" },
              { title: "Global knowledge, local wisdom", desc: "Japanese joinery, Senegalese wrestling, Kerala Ayurvedic massage, Icelandic Glima, Brazilian Capoeira — traditions that textbooks forgot.", accent: "from-amber-500/20 to-amber-600/5" },
            ].map((item, i) => (
              <div key={i} className={`bg-gradient-to-br ${item.accent} border border-zinc-800/50 rounded-2xl p-6 hover:border-zinc-700/50 transition-all group hover:translate-y-[-2px]`}>
                <h3 className="font-bold text-white text-sm mb-2">{item.title}</h3>
                <p className="text-xs text-zinc-400 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════ HOW IT WORKS ════════════════ */}
      <section className="py-16 sm:py-20 border-t border-zinc-900" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-600 mb-3">How it works</p>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white">Three steps. Zero complexity.</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              { step: '01', title: 'Build your tree', desc: 'Pick your industry. Drill into your niche. Add branches for every skill you can teach. Upload proof to get verified.', icon: Layers },
              { step: '02', title: 'Get found', desc: "People search for problems. Our engine finds you by expertise match, not follower count. The best answer wins.", icon: Search },
              { step: '03', title: 'Get paid', desc: 'Set your price for DMs, voice notes, or video calls. Instant wallet payments or Razorpay checkout. No gatekeepers.', icon: TrendingUp },
            ].map((s, i) => (
              <div key={i} className="relative">
                <div className="text-5xl font-black text-zinc-900 mb-4">{s.step}</div>
                <div className="w-12 h-12 rounded-2xl bg-zinc-900 border border-zinc-800 flex items-center justify-center mb-4">
                  <s.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-bold text-white mb-2">{s.title}</h3>
                <p className="text-sm text-zinc-500 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════ EARN SECTION ════════════════ */}
      <section className="py-16 sm:py-20 border-t border-zinc-900" data-testid="monetization-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-600 mb-3">Earn from what you know</p>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-2">Five ways to monetize. You pick.</h2>
          <p className="text-sm text-zinc-500 mb-10 max-w-lg">Set individual prices for each. Go as low or high as your expertise commands. Pay from wallet or UPI — we support QR payments too.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { icon: MessageCircle, title: 'Paid DMs', price: 'From ₹49/msg', desc: 'Quick text advice on your schedule', color: 'border-blue-500/30 hover:border-blue-500/60' },
              { icon: Phone, title: 'Voice Notes', price: 'From ₹99/note', desc: 'Async audio answers — thoughtful, personal', color: 'border-green-500/30 hover:border-green-500/60' },
              { icon: Phone, title: 'Voice Calls', price: 'From ₹299/call', desc: 'Live voice sessions — real-time problem solving', color: 'border-purple-500/30 hover:border-purple-500/60' },
              { icon: Video, title: 'Video Calls', price: 'From ₹499/call', desc: 'Face-to-face premium consultations with whiteboard', color: 'border-orange-500/30 hover:border-orange-500/60' },
              { icon: Users, title: 'Group Sessions', price: 'From ₹99/seat', desc: 'Host workshops, AMAs, masterclasses for multiple learners', color: 'border-pink-500/30 hover:border-pink-500/60' },
            ].map((ch, i) => (
              <div key={i} className={`bg-zinc-950 border ${ch.color} rounded-2xl p-6 transition-all hover:translate-y-[-2px]`}>
                <ch.icon className="w-7 h-7 text-white mb-3" />
                <h3 className="font-bold text-white text-sm">{ch.title}</h3>
                <p className="text-xs text-emerald-400 font-semibold mb-2">{ch.price}</p>
                <p className="text-xs text-zinc-500">{ch.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════ TAXONOMY SHOWCASE ════════════════ */}
      <section className="py-16 sm:py-20 border-t border-zinc-900" data-testid="industries-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-zinc-600 mb-3">The world's deepest expertise map</p>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-2">
                {industryCount} industries. {nodeCount.toLocaleString()} things you can teach or learn.
              </h2>
              <p className="text-sm text-zinc-500 max-w-lg">
                Every college subject on the planet. Every traditional art. Every sport — modern and ancient. Every language ever taught. Every research field. Every craft your grandmother knew. And if it's not here — you can add it.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 mb-8 mt-6">
            <button onClick={() => navigate('/explore')}
              className="bg-white text-black font-bold text-sm rounded-full px-6 py-2.5 hover:bg-zinc-200 transition-all flex items-center space-x-2"
              data-testid="explore-taxonomy-btn">
              <span>Explore Full Taxonomy</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
            {Object.keys(taxonomy).slice(0, 20).map(ind => (
              <button key={ind} onClick={() => navigate('/explore')}
                className="flex items-center justify-between p-3.5 rounded-xl bg-zinc-950 border border-zinc-800/60 hover:border-zinc-700 hover:bg-zinc-900/50 transition-all text-left group"
                data-testid={`landing-ind-${ind}`}>
                <span className="text-xs font-medium text-zinc-400 group-hover:text-white transition-colors truncate">{ind}</span>
                <ChevronRight className="w-3.5 h-3.5 text-zinc-800 group-hover:text-zinc-500 transition-colors shrink-0 ml-1" />
              </button>
            ))}
          </div>
          {Object.keys(taxonomy).length > 20 && (
            <div className="text-center mt-5">
              <button onClick={() => navigate('/explore')} className="text-xs text-zinc-600 hover:text-white transition-colors" data-testid="see-all-industries-btn">
                + {Object.keys(taxonomy).length - 20} more industries
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ════════════════ WHO IS THIS FOR ════════════════ */}
      <section className="py-16 sm:py-20 border-t border-zinc-900" data-testid="who-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-600 mb-3">Who should join</p>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-10">If you know something valuable, this is your platform.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              "A retired IAS officer who can mentor civil service aspirants",
              "A Korean grandma who makes the best kimchi in her village",
              "A day trader who turned ₹50K into ₹50L",
              "A classical dancer preserving a dying art form",
              "A PhD student who just cracked the toughest exam in her country",
              "A carpenter who builds furniture without a single nail",
              "A yoga instructor in Rishikesh with 30 years of practice",
              "A cybersecurity expert who hunts bugs for a living",
              "A farmer who grew organic saffron at 10,000 feet altitude",
            ].map((text, i) => (
              <div key={i} className="flex items-start space-x-3 p-4 rounded-xl bg-zinc-950 border border-zinc-800/40 hover:border-zinc-700/60 transition-all">
                <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                <p className="text-sm text-zinc-300 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════ DATA PRIVACY & REFUND POLICY ════════════════ */}
      <section className="py-16 sm:py-20 border-t border-zinc-900" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-600 mb-3">Trust & Safety</p>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-10">Your trust is our foundation.</h2>
          <div className="grid sm:grid-cols-2 gap-6 max-w-4xl">
            {/* Data Privacy */}
            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-8 hover:border-emerald-500/30 transition-all" data-testid="privacy-card">
              <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-5">
                <Lock className="w-6 h-6 text-emerald-400" />
              </div>
              <h3 className="font-bold text-white text-lg mb-3">Data Privacy</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-2.5">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                  <span className="text-sm text-zinc-400"><strong className="text-white">End-to-end encryption</strong> on all calls and messages. We cannot see or hear your conversations.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                  <span className="text-sm text-zinc-400"><strong className="text-white">No data selling.</strong> We will never sell your personal information or browsing data to third parties. Ever.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                  <span className="text-sm text-zinc-400"><strong className="text-white">Your data, your control.</strong> Download or delete all your data anytime from your profile settings.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                  <span className="text-sm text-zinc-400"><strong className="text-white">No tracking across the web.</strong> We don't follow you outside Connect. No cross-site trackers.</span>
                </li>
              </ul>
            </div>

            {/* 100% Refund Policy */}
            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-8 hover:border-purple-500/30 transition-all" data-testid="refund-card">
              <div className="w-12 h-12 rounded-2xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center mb-5">
                <RefreshCw className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="font-bold text-white text-lg mb-3">100% Refund Policy</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-2.5">
                  <CheckCircle className="w-4 h-4 text-purple-400 mt-0.5 shrink-0" />
                  <span className="text-sm text-zinc-400"><strong className="text-white">Expert didn't show up?</strong> Full refund. No questions asked. Automatically processed within 24 hours.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle className="w-4 h-4 text-purple-400 mt-0.5 shrink-0" />
                  <span className="text-sm text-zinc-400"><strong className="text-white">Not satisfied with the call?</strong> Request a full refund within 24 hours of the session. We'll handle it.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle className="w-4 h-4 text-purple-400 mt-0.5 shrink-0" />
                  <span className="text-sm text-zinc-400"><strong className="text-white">Technical issues?</strong> If call quality was poor due to platform issues, you get an automatic full refund or a free rebooking.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle className="w-4 h-4 text-purple-400 mt-0.5 shrink-0" />
                  <span className="text-sm text-zinc-400"><strong className="text-white">Ghosting insurance.</strong> Every payment is held for 24 hours before release. Your money is always protected.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════ TRUST BAR ════════════════ */}
      <section className="py-10 border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            {[
              { icon: Shield, label: 'Verified Experts', desc: 'Certificate-backed proof' },
              { icon: Award, label: 'Deep Taxonomy', desc: `${nodeCount.toLocaleString()} micro-niches` },
              { icon: Star, label: 'Real Reviews', desc: 'From real sessions' },
              { icon: Lock, label: '100% Refund', desc: 'No questions asked' },
            ].map((t, i) => (
              <div key={i} className="flex flex-col items-center">
                <div className="w-10 h-10 rounded-xl bg-zinc-900 border border-zinc-800 flex items-center justify-center mb-2">
                  <t.icon className="w-5 h-5 text-zinc-400" />
                </div>
                <p className="text-xs font-bold text-white">{t.label}</p>
                <p className="text-[11px] text-zinc-600">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════ SUGGESTION BOX ════════════════ */}
      <section className="py-16 sm:py-20 border-t border-zinc-900" data-testid="suggestion-section">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-600 mb-3">We're listening</p>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-3">Got an idea? Tell us.</h2>
          <p className="text-sm text-zinc-500 mb-8 max-w-md mx-auto">We build what you need. Drop a suggestion and help shape the future of Connect.</p>
          <SuggestionBox />
        </div>
      </section>

      {/* ════════════════ FINAL CTA ════════════════ */}
      <section className="py-20 sm:py-28 border-t border-zinc-900 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-purple-500/[0.03] rounded-full blur-[100px]" />
        </div>
        <div className="max-w-3xl mx-auto px-4 text-center relative">
          <p className="text-sm text-zinc-500 italic mb-6">"Knowledge is the only treasure that multiplies when you share it."</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-4 leading-tight">
            Stop scrolling.<br/>Start sharing what you know.
          </h2>
          <p className="text-zinc-400 mb-3 text-base sm:text-lg max-w-lg mx-auto">
            Your experience is worth more than you think. Someone out there would pay to hear it.
          </p>
          <p className="text-xs text-zinc-600 mb-8">Free to join. Set your own prices. 100% refund guarantee. Get paid instantly.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button onClick={() => navigate('/signup')}
              className="bg-white text-black font-bold text-base rounded-full px-10 py-4 hover:bg-zinc-200 transition-all shadow-[0_0_40px_rgba(168,85,247,0.1)]"
              data-testid="cta-signup">
              Create Your Profile — It's Free
            </button>
            <button onClick={() => navigate('/explore')}
              className="text-sm text-zinc-500 hover:text-white transition-colors"
              data-testid="cta-explore">
              or explore {nodeCount.toLocaleString()} subjects
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-zinc-900 text-zinc-600 text-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 bg-gradient-to-br from-purple-500 to-orange-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-[10px]">C</span>
            </div>
            <span className="font-bold text-zinc-400">Connect</span>
          </div>
          <div className="flex items-center space-x-4">
            <span>Privacy Policy</span>
            <span>Terms of Service</span>
            <span>Refund Policy</span>
          </div>
          <p>2026 Connect. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
